/**
 * Network Monitor Service
 * 
 * Monitors network connectivity and provides reconnect functionality
 * for the mobile app to handle WiFi disconnections gracefully.
 */

class NetworkMonitor {
  constructor() {
    this.isOnline = navigator.onLine;
    this.listeners = [];
    this.reconnectCallbacks = [];
    
    // Listen to browser online/offline events
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));
    
    console.log('[NetworkMonitor] Initialized, online status:', this.isOnline);
  }

  handleOnline() {
    console.log('[NetworkMonitor] Network is ONLINE');
    this.isOnline = true;
    this.notifyListeners('online');
    this.triggerReconnect();
  }

  handleOffline() {
    console.log('[NetworkMonitor] Network is OFFLINE');
    this.isOnline = false;
    this.notifyListeners('offline');
  }

  /**
   * Add a listener for network status changes
   * @param {Function} callback - Called with ('online' | 'offline')
   */
  addListener(callback) {
    this.listeners.push(callback);
    // Immediately notify of current status
    callback(this.isOnline ? 'online' : 'offline');
  }

  /**
   * Remove a listener
   */
  removeListener(callback) {
    this.listeners = this.listeners.filter(cb => cb !== callback);
  }

  /**
   * Notify all listeners of status change
   */
  notifyListeners(status) {
    this.listeners.forEach(callback => {
      try {
        callback(status);
      } catch (error) {
        console.error('[NetworkMonitor] Listener error:', error);
      }
    });
  }

  /**
   * Register a callback to run when network reconnects
   * @param {Function} callback - Called when network comes back online
   */
  onReconnect(callback) {
    this.reconnectCallbacks.push(callback);
  }

  /**
   * Trigger all reconnect callbacks
   */
  triggerReconnect() {
    console.log('[NetworkMonitor] Triggering reconnect callbacks:', this.reconnectCallbacks.length);
    this.reconnectCallbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('[NetworkMonitor] Reconnect callback error:', error);
      }
    });
  }

  /**
   * Check if network is currently online
   */
  getStatus() {
    return this.isOnline;
  }

  /**
   * Test connection by making a ping request
   * @param {string} apiUrl - Base API URL
   */
  async testConnection(apiUrl) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${apiUrl}/health`, {
        method: 'HEAD',
        signal: controller.signal,
        cache: 'no-cache'
      });

      clearTimeout(timeoutId);
      return response.ok;
    } catch (error) {
      console.log('[NetworkMonitor] Connection test failed:', error.message);
      return false;
    }
  }
}

// Export singleton instance
const networkMonitor = new NetworkMonitor();
export default networkMonitor;
